<template>
  <div class="home">
    <!-- Hero Section -->
    <header v-if="!isAuthenticated" class="py-5 position-relative">
        <nav class="navbar navbar-light position-absolute top-0 start-0 w-100 p-3">
            <div class="container d-flex justify-content-between align-items-center">
                <span class="navbar-brand fw-bold text-primary">{{ $t('navbar.brand') }}</span>
                <div>
                    <button class="btn btn-outline-primary btn-sm rounded-pill px-3 me-2" @click="toggleTheme" title="Toggle Theme">
                        <span v-if="!darkMode">🌙</span>
                        <span v-else>☀️</span>
                    </button>
                    <button class="btn btn-outline-primary btn-sm rounded-pill px-3" @click="toggleLanguage" title="Switch Language">
                        {{ $i18n.locale === 'ro' ? 'EN' : 'RO' }}
                    </button>
                </div>
            </div>
        </nav>

        <div class="container text-center py-5 mt-4">
            <span class="badge rounded-pill bg-light text-primary text-uppercase tracking-wide px-3 py-2 mb-3 shadow-sm border">
                {{ $t('home.badge') }}
            </span>
            <h1 class="display-4 fw-bold mt-3 mb-4">
                {{ $t('home.title_prefix') }} <span class="text-primary">{{ $t('home.title_highlight') }}</span><br class="d-none d-md-inline"> {{ $t('home.title_suffix') }}
            </h1>
            <p class="lead text-muted mx-auto mb-5" style="max-width: 800px;">
                {{ $t('home.subtitle') }}
            </p>
            
            <div class="d-flex justify-content-center gap-3 mb-5">
                <router-link to="/register" class="btn btn-primary btn-lg px-4 py-3 fw-bold shadow-sm">
                    {{ $t('home.buttons.start') }}
                </router-link>
                <router-link to="/login" class="btn btn-success btn-lg px-4 py-3 fw-bold text-white shadow-sm">
                    {{ $t('home.buttons.login') }}
                </router-link>
            </div>
            
            <div class="row justify-content-center mt-5">
                <div class="col-md-4 mb-4 mb-md-0 px-4">
                    <div class="d-flex flex-column align-items-center">
                        <span class="text-primary mb-2 fs-4">
                            <i class="bi bi-check-circle-fill"></i>
                        </span>
                        <h6 class="fw-bold mb-2">{{ $t('home.trust.no_card') }}</h6>
                        <p class="text-muted small px-3">{{ $t('home.trust.descriptions.no_card', { limit: dailyFreeLimit }) }}</p>
                    </div>
                </div>
                <div class="col-md-4 mb-4 mb-md-0 px-4 border-start-md border-end-md">
                     <div class="d-flex flex-column align-items-center">
                        <span class="text-primary mb-2 fs-4">
                            <i class="bi bi-check-circle-fill"></i>
                        </span>
                        <h6 class="fw-bold mb-2">{{ $t('home.trust.setup') }}</h6>
                        <p class="text-muted small px-3">{{ $t('home.trust.descriptions.setup') }}</p>
                    </div>
                </div>
                <div class="col-md-4 px-4">
                     <div class="d-flex flex-column align-items-center">
                        <span class="text-primary mb-2 fs-4">
                            <i class="bi bi-check-circle-fill"></i>
                        </span>
                        <h6 class="fw-bold mb-2">{{ $t('home.trust.gdpr') }}</h6>
                        <p class="text-muted small px-3">{{ $t('home.trust.descriptions.gdpr') }}</p>
                    </div>
                </div>
            </div>
        </div>
    </header>

    <!-- Main App Button when Logged In -->
    <div v-else class="container text-center py-5 my-5">
        <h1 class="display-4 mb-4">Welcome back!</h1>
        <router-link to="/invoice" class="btn btn-primary btn-lg px-5 py-3 shadow">
            {{ $t('home.process_invoice') }} <i class="bi bi-arrow-right ms-2"></i>
        </router-link>
    </div>

    <!-- Comparison Section -->
    <section v-if="!isAuthenticated" class="py-5">
        <div class="container mb-5 text-center">
            <h2 class="fw-bold mb-5">{{ $t('home.comparison.title') }}</h2>
            
            <div class="row g-4 justify-content-center">
                <!-- Before Card -->
                <div class="col-md-6 col-lg-5">
                    <div class="card h-100 border-danger border-opacity-25 bg-danger bg-opacity-10 rounded-4 shadow-sm border-2">
                        <div class="card-body p-4 text-start">
                            <h4 class="text-danger fw-bold mb-4 d-flex align-items-center">
                                <i class="bi bi-x-circle-fill me-2 fs-4"></i> {{ $t('home.comparison.before.title') }}
                            </h4>
                            <ul class="list-unstyled text-muted mb-0">
                                <li class="mb-3 d-flex"><i class="bi bi-dash me-2"></i> {{ $t('home.comparison.before.item1') }}</li>
                                <li class="mb-3 d-flex"><i class="bi bi-dash me-2"></i> {{ $t('home.comparison.before.item2') }}</li>
                                <li class="mb-3 d-flex"><i class="bi bi-dash me-2"></i> {{ $t('home.comparison.before.item3') }}</li>
                                <li class="d-flex"><i class="bi bi-dash me-2"></i> {{ $t('home.comparison.before.item4') }}</li>
                            </ul>
                        </div>
                    </div>
                </div>
                
                <!-- After Card -->
                <div class="col-md-6 col-lg-5">
                    <div class="card h-100 border-success border-opacity-25 bg-success bg-opacity-10 rounded-4 shadow-sm border-2">
                        <div class="card-body p-4 text-start">
                            <h4 class="text-success fw-bold mb-4 d-flex align-items-center">
                                <i class="bi bi-check-circle-fill me-2 fs-4"></i> {{ $t('home.comparison.after.title') }}
                            </h4>
                            <ul class="list-unstyled text-secondary mb-0">
                                <li class="mb-3 d-flex fw-bold"><i class="bi bi-plus me-2 text-success"></i> {{ $t('home.comparison.after.item1') }}</li>
                                <li class="mb-3 d-flex fw-bold"><i class="bi bi-plus me-2 text-success"></i> {{ $t('home.comparison.after.item2') }}</li>
                                <li class="mb-3 d-flex fw-bold"><i class="bi bi-plus me-2 text-success"></i> {{ $t('home.comparison.after.item3') }}</li>
                                <li class="d-flex fw-bold"><i class="bi bi-plus me-2 text-success"></i> {{ $t('home.comparison.after.item4') }}</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- Pricing Section -->
    <section v-if="!isAuthenticated" class="py-5">
        <div class="container mb-5">
            <div class="text-center mb-5">
                <h2 class="fw-bold mb-3 d-inline-block">{{ $t('home.pricing.title') }}</h2>
                <p class="text-muted fst-italic">{{ $t('home.pricing.subtitle') }}</p>
            </div>

            <div class="row justify-content-center">
                <div class="col-lg-10">
                    <div class="row g-4 justify-content-center">
                        <!-- Free Plan -->
                        <div class="col-md-6 col-lg-4">
                            <div class="card h-100 border-0 shadow-sm hover-shadow transition-all rounded-4">
                                <div class="card-body p-4 d-flex flex-column">
                                    <h4 class="fw-bold mb-1">{{ $t('home.pricing.free.title') }}</h4>
                                    <p class="text-muted small fst-italic mb-4">{{ $t('home.pricing.free.subtitle') }}</p>
                                    
                                    <div class="mb-4">
                                        <span class="h1 fw-bold">{{ $t('home.pricing.free.price') }}</span>
                                    </div>
                                    
                                    <ul class="list-unstyled mb-4 flex-grow-1">
                                        <li class="mb-3 d-flex align-items-center text-secondary">
                                            <i class="bi bi-check-lg text-success me-2"></i> {{ $t('home.pricing.free.features.item1', { limit: dailyFreeLimit }) }}
                                        </li>
                                        <li class="mb-3 d-flex align-items-center text-secondary">
                                            <i class="bi bi-check-lg text-success me-2"></i> {{ $t('home.pricing.free.features.item2') }}
                                        </li>
                                        <li class="d-flex align-items-center text-secondary">
                                            <i class="bi bi-check-lg text-success me-2"></i> {{ $t('home.pricing.free.features.item3') }}
                                        </li>
                                    </ul>
                                    
                                    <router-link to="/register" class="btn btn-outline-primary fw-bold w-100 py-2 rounded-3">
                                        {{ $t('home.pricing.free.button') }}
                                    </router-link>
                                </div>
                            </div>
                        </div>

                        <!-- Standard Plan -->
                        <div class="col-md-6 col-lg-4">
                            <div class="card h-100 border-primary border-2 shadow hover-shadow transition-all rounded-4 position-relative" style="top: -10px;">
                                <span class="position-absolute top-0 start-50 translate-middle badge rounded-pill bg-primary px-3 py-1 shadow-sm uppercase-xs">
                                    {{ $t('home.pricing.standard.badge') }}
                                </span>
                                <div class="card-body p-4 d-flex flex-column">
                                    <h4 class="fw-bold mb-1 mt-2">{{ $t('home.pricing.standard.title') }}</h4>
                                    <p class="text-muted small fst-italic mb-4">{{ $t('home.pricing.standard.subtitle') }}</p>
                                    
                                    <div class="mb-4">
                                        <span class="h1 fw-bold">${{ creditCost }}</span>
                                        <span class="text-muted small">{{ $t('home.pricing.standard.unit') }}</span>
                                    </div>
                                    
                                    <ul class="list-unstyled mb-4 flex-grow-1">
                                        <li class="mb-3 d-flex align-items-center text-secondary">
                                            <i class="bi bi-check-lg text-success me-2"></i> {{ $t('home.pricing.standard.features.item1') }}
                                        </li>
                                        <li class="mb-3 d-flex align-items-center text-secondary">
                                            <i class="bi bi-check-lg text-success me-2"></i> {{ $t('home.pricing.free.features.item2') }}
                                        </li> 
                                        <li class="mb-3 d-flex align-items-center text-secondary">
                                            <i class="bi bi-check-lg text-success me-2"></i> {{ $t('home.pricing.free.features.item3') }}
                                        </li>

                                        <li class="mb-3 d-flex align-items-center text-secondary">
                                            <i class="bi bi-check-lg text-success me-2"></i> {{ $t('home.pricing.standard.features.item2') }}
                                        </li>
                                       
                                    </ul>
                                    
                                    <router-link to="/register" class="btn btn-primary fw-bold w-100 py-2 rounded-3 shadow-sm">
                                        {{ $t('home.pricing.standard.button') }}
                                    </router-link>
                                </div>
                            </div>
                        </div>

                        <!-- Corporate Plan -->
                        <div class="col-md-6 col-lg-4">
                            <div class="card h-100 border-0 shadow-sm hover-shadow transition-all rounded-4">
                                <div class="card-body p-4 d-flex flex-column">
                                    <h4 class="fw-bold mb-1">{{ $t('home.pricing.corporate.title') }}</h4>
                                    <p class="text-muted small fst-italic mb-4">{{ $t('home.pricing.corporate.subtitle') }}</p>
                                    
                                    <div class="mb-4">
                                        <span class="h2 fw-bold">{{ $t('home.pricing.corporate.price') }}</span>
                                    </div>
                                    
                                    <ul class="list-unstyled mb-4 flex-grow-1">
                                        <li class="mb-3 d-flex align-items-center text-secondary">
                                            <i class="bi bi-check-lg text-success me-2"></i> {{ $t('home.pricing.corporate.features.item1') }}
                                        </li>
                                        <li class="mb-3 d-flex align-items-center text-secondary">
                                            <i class="bi bi-check-lg text-success me-2"></i> {{ $t('home.pricing.corporate.features.item2') }}
                                        </li>
                                        <li class="mb-3 d-flex align-items-center text-secondary">
                                            <i class="bi bi-check-lg text-success me-2"></i> {{ $t('home.pricing.corporate.features.item3') }}
                                        </li>
                                        <li class="d-flex align-items-center text-secondary">
                                            <i class="bi bi-check-lg text-success me-2"></i> {{ $t('home.pricing.corporate.features.item4') }}
                                        </li>
                                    </ul>
                                    
                                    <a href="#" @click.prevent="$el.querySelector('.contact-form-section').scrollIntoView({behavior: 'smooth'})" class="btn btn-dark fw-bold w-100 py-2 rounded-3">
                                        {{ $t('home.pricing.corporate.button') }}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <p class="text-center mt-5 text-muted small fst-italic">{{ $t('home.pricing.disclaimer') }}</p>
        </div>
    </section>

    <!-- Static Company & Contact Section -->
    <div v-if="!isAuthenticated" class="container contact-form-section">
      <div class="row justify-content-center">
        <div class="col-lg-10">
            <div class="row mb-5 p-4 rounded shadow-sm border mx-auto">
              <div class="col-md-6 mb-4 mb-md-0">
                <h3 class="mb-3">{{ $t('company.title') }}</h3>
                <p class="text-justify text-muted">{{ $t('company.description') }}</p>
                
                <h5 class="mt-4 border-bottom pb-2">{{ $t('company.details_title') }}</h5>
                <div class="ps-2 small">
                    <p class="mb-1 fw-bold">{{ $t('company.name') }}</p>
                    <p class="mb-1">{{ $t('company.address') }}</p>
                    <p class="mb-1">{{ $t('company.cif') }}</p>
                    <p class="mb-1">{{ $t('company.reg') }}</p>
                    <p class="mb-1">{{ $t('company.tel') }}</p>
                    <p class="mb-1">{{ $t('company.email') }}</p>
                </div>
              </div>
              
              <div class="col-md-6">
                <h3 class="mb-3">{{ $t('contact.title') }}</h3>
                <form @submit.prevent="sendMessage">
                  <div class="mb-3">
                     <label class="form-label small text-muted">{{ $t('contact.name') }}</label>
                     <input type="text" class="form-control" v-model="contactForm.name" required>
                  </div>
                  <div class="row">
                      <div class="col-md-6 mb-3">
                          <label class="form-label small text-muted">{{ $t('contact.email') }}</label>
                          <input type="email" class="form-control" v-model="contactForm.email" required>
                      </div>
                      <div class="col-md-6 mb-3">
                          <label class="form-label small text-muted">{{ $t('contact.phone') }}</label>
                          <input type="tel" class="form-control" v-model="contactForm.phone">
                      </div>
                  </div>
                  <div class="mb-3">
                      <label class="form-label small text-muted">{{ $t('contact.message') }}</label>
                      <textarea class="form-control" rows="4" v-model="contactForm.message" required></textarea>
                  </div>
                  
                  <button type="submit" class="btn btn-primary w-100" :disabled="sending">
                      <span v-if="sending" class="spinner-border spinner-border-sm me-2"></span>
                      {{ sending ? $t('contact.sending') : $t('contact.submit') }}
                  </button>
                  
                  <div v-if="contactSuccess" class="alert alert-success mt-3 py-2 small">
                     <i class="bi bi-check-circle me-1"></i> {{ $t('contact.success') }}
                  </div>
                  <div v-if="contactError" class="alert alert-danger mt-3 py-2 small">
                     <i class="bi bi-exclamation-circle me-1"></i> {{ $t('contact.error') }}
                  </div>
                </form>
              </div>
            </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import axios from '@/axios'

export default {
  name: 'Home',
  data() {
    return {
      contactForm: {
        name: '',
        email: '',
        phone: '',
        message: ''
      },
      sending: false,
      contactSuccess: false,
      contactError: false,
      creditCost: 0.1, // Default fallback
      dailyFreeLimit: 10, // Default fallback
      darkMode: false
    }
  },
  async created() {
    // Check local storage for theme
    const savedTheme = localStorage.getItem('user-theme');
    if (savedTheme) {
        this.darkMode = savedTheme === 'dark';
        document.documentElement.setAttribute('data-bs-theme', savedTheme);
    }

    try {
      const response = await axios.get('/api/config/company');
      if (response.data) {
        if (response.data.credit_cost) {
            this.creditCost = response.data.credit_cost;
        }
        if (response.data.daily_free_limit) {
            this.dailyFreeLimit = response.data.daily_free_limit;
        }
      }
    } catch (error) {
      console.error("Failed to load config:", error);
    }
  },
  computed: {
    ...mapGetters('auth', ['isAuthenticated'])
  },
  methods: {
    toggleLanguage() {
      const newLang = this.$i18n.locale === 'ro' ? 'en' : 'ro';
      this.$i18n.locale = newLang;
      localStorage.setItem('user-language', newLang);
    },
    toggleTheme() {
      this.darkMode = !this.darkMode;
      const theme = this.darkMode ? 'dark' : 'light';
      document.documentElement.setAttribute('data-bs-theme', theme);
      localStorage.setItem('user-theme', theme);
    },
    async sendMessage() {
      this.sending = true;
      this.contactSuccess = false;
      this.contactError = false;
      
      try {
        await axios.post('/api/util/contact', this.contactForm);
        this.contactSuccess = true;
        this.contactForm = { name: '', email: '', phone: '', message: '' }; // Reset
      } catch (err) {
        console.error("Contact form error:", err);
        this.contactError = true;
      } finally {
        this.sending = false;
      }
    }
  }
}
</script>

<style scoped>
/* Custom gradients to mimic the design */
.bg-gradient-white-light {
    background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%);
}
.tracking-wide {
    letter-spacing: 0.1em;
}
.rounded-4 {
    border-radius: 1rem !important;
}

/* Custom gradients to mimic the design */
.bg-gradient-white-light {
    background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%);
}
.tracking-wide {
    letter-spacing: 0.1em;
}
.rounded-4 {
    border-radius: 1rem !important;
}

/* Responsive borders: only on medium screens and up */
@media (min-width: 768px) {
    .border-start-md {
        border-left: 1px solid #dee2e6;
    }
    .border-end-md {
        border-right: 1px solid #dee2e6;
    }
}
</style>